import { useState, useEffect, useCallback } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { html as htmlLang } from '@codemirror/lang-html';
import { IconDeviceFloppy, IconRefresh } from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthContext';
import {
  getEmailTemplate,
  saveEmailTemplate,
  getGeneralSettings,
  saveGeneralSettings,
} from '../services/settingsService';
import type { QuoteEmailTemplate, GeneralSettings, ExternalUrlsSettings } from '../types';

// ─── Tab type ─────────────────────────────────────────────────────────────────

type Tab = 'emailTemplate' | 'general';

// ─── Email template helpers ───────────────────────────────────────────────────

const SAMPLE_CONTEXT: Record<string, string> = {
  '{{quote.serviceName}}': 'Abate e limpeza florestal',
  '{{quote.requestedAt}}': '10/03/2026',
  '{{quote.location.district}}': 'Aveiro',
  '{{quote.location.county}}': 'Águeda',
  '{{quote.location.parish}}': 'Borralha',
  '{{supplier.name}}': 'Florestação Silva, Lda.',
  '{{supplier.contactEmail}}': 'geral@florestacaosilva.pt',
  '{{supplier.contactPhone}}': '+351 234 000 000',
  '{{requestor.name}}': 'João Pereira',
  '{{requestor.email}}': 'joao.pereira@example.com',
};

function renderPreview(template: string): string {
  let result = template;
  for (const [token, value] of Object.entries(SAMPLE_CONTEXT)) {
    result = result.split(token).join(value);
  }
  result = result.replace(/\{\{#if [^}]+\}\}([\s\S]*?)\{\{\/if\}\}/g, '$1');
  return result;
}

const TOKEN_GROUPS = [
  {
    label: 'Quote',
    tokens: [
      { token: '{{quote.serviceName}}', description: 'Service name' },
      { token: '{{quote.requestedAt}}', description: 'Date of request (DD/MM/YYYY)' },
      { token: '{{quote.location.district}}', description: 'District' },
      { token: '{{quote.location.county}}', description: 'County' },
      { token: '{{quote.location.parish}}', description: 'Parish (may be empty)' },
    ],
  },
  {
    label: 'Supplier',
    tokens: [
      { token: '{{supplier.name}}', description: 'Supplier display name' },
      { token: '{{supplier.contactEmail}}', description: 'Supplier email address' },
      { token: '{{supplier.contactPhone}}', description: 'Supplier phone (may be empty)' },
    ],
  },
  {
    label: 'Requestor',
    tokens: [
      { token: '{{requestor.name}}', description: 'Requestor full name' },
      { token: '{{requestor.email}}', description: 'Requestor email address' },
    ],
  },
];

// ─── Email Template Tab ───────────────────────────────────────────────────────

function EmailTemplateTab() {
  const { user } = useAuth();

  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<QuoteEmailTemplate | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getEmailTemplate()
      .then(template => {
        if (cancelled) return;
        if (template) {
          setSubject(template.subject);
          setHtmlBody(template.html);
          setLastSaved(template);
        }
      })
      .catch(err => {
        if (!cancelled) setError('Failed to load template: ' + (err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = useCallback(async () => {
    if (!user?.email) return;
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await saveEmailTemplate({ subject, html: htmlBody }, user.email);
      const saved: QuoteEmailTemplate = {
        subject,
        html: htmlBody,
        updatedAt: new Date().toISOString(),
        updatedBy: user.email,
      };
      setLastSaved(saved);
      setSuccessMessage('Template saved successfully.');
    } catch (err) {
      setError('Failed to save template: ' + (err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [subject, htmlBody, user?.email]);

  const handleDiscard = useCallback(() => {
    if (!lastSaved) return;
    setSubject(lastSaved.subject);
    setHtmlBody(lastSaved.html);
    setError(null);
    setSuccessMessage(null);
  }, [lastSaved]);

  const isDirty =
    lastSaved !== null && (subject !== lastSaved.subject || htmlBody !== lastSaved.html);

  const preview = renderPreview(htmlBody);

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        {isDirty && (
          <button
            type="button"
            className="btn btn-outline-secondary me-2"
            onClick={handleDiscard}
            disabled={saving}
          >
            <IconRefresh className="icon me-1" />
            Discard
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || loading}
        >
          <IconDeviceFloppy className="icon me-1" />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success" role="alert">
          {successMessage}
        </div>
      )}
      {lastSaved && (
        <div className="text-muted small mb-3">
          Last saved: {new Date(lastSaved.updatedAt).toLocaleString()} by {lastSaved.updatedBy}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {/* ── Left column: editor ── */}
          <div className="col-12 col-xl-6">
            <div className="card mb-4">
              <div className="card-header">
                <h3 className="card-title">Subject Line</h3>
              </div>
              <div className="card-body">
                <label className="form-label" htmlFor="email-subject">
                  Subject
                </label>
                <input
                  id="email-subject"
                  type="text"
                  className="form-control"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="e.g. Novo Pedido de Orçamento — {{quote.serviceName}}"
                />
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h3 className="card-title">HTML Body</h3>
              </div>
              <div className="card-body p-0">
                <CodeMirror
                  value={htmlBody}
                  extensions={[htmlLang()]}
                  onChange={setHtmlBody}
                  theme="light"
                  minHeight="400px"
                  style={{ fontSize: '13px' }}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Available Tokens</h3>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>Token</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {TOKEN_GROUPS.map(group => (
                        <>
                          <tr key={`group-${group.label}`} className="table-secondary">
                            <td colSpan={2} className="fw-bold">
                              {group.label}
                            </td>
                          </tr>
                          {group.tokens.map(t => (
                            <tr key={t.token}>
                              <td>
                                <code style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                  {t.token}
                                </code>
                              </td>
                              <td className="text-muted">{t.description}</td>
                            </tr>
                          ))}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-footer text-muted small">
                  Use <code>{'{{#if quote.location.parish}}…{{/if}}'}</code> for optional fields.
                </div>
              </div>
            </div>
          </div>

          {/* ── Right column: live preview ── */}
          <div className="col-12 col-xl-6">
            <div className="card h-100">
              <div className="card-header">
                <h3 className="card-title">Preview</h3>
                <div className="card-options">
                  <span className="badge bg-secondary">Sample data</span>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="border-bottom px-3 py-2 bg-light">
                  <span className="text-muted small me-2">Subject:</span>
                  <strong style={{ fontSize: '14px' }}>
                    {renderPreview(subject) || '(no subject)'}
                  </strong>
                </div>
                <iframe
                  srcDoc={preview}
                  title="Email preview"
                  style={{ width: '100%', minHeight: '600px', border: 'none' }}
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── General Settings Tab ─────────────────────────────────────────────────────

function GeneralSettingsTab() {
  const { user } = useAuth();

  const [supportEmail, setSupportEmail] = useState('');
  const [externalUrls, setExternalUrls] = useState<ExternalUrlsSettings>({
    '2bforest': { id: '2bforest', name: '2BForest', url: '', topBarColor: '#101828' },
    'forest-sim': { id: 'forest-sim', name: 'Forest Sim', url: '', topBarColor: '#F39200' },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<GeneralSettings | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getGeneralSettings()
      .then(settings => {
        if (cancelled) return;
        if (settings) {
          setSupportEmail(settings.supportEmail);
          setExternalUrls(settings.externalUrls);
          setLastSaved(settings);
        }
      })
      .catch(err => {
        if (!cancelled) setError('Failed to load settings: ' + (err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = useCallback(async () => {
    if (!user?.email) return;
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await saveGeneralSettings({ supportEmail, externalUrls }, user.email);
      const saved: GeneralSettings = {
        supportEmail,
        externalUrls,
        updatedAt: new Date().toISOString(),
        updatedBy: user.email,
      };
      setLastSaved(saved);
      setSuccessMessage('Settings saved successfully.');
    } catch (err) {
      setError('Failed to save settings: ' + (err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [supportEmail, externalUrls, user?.email]);

  const handleDiscard = useCallback(() => {
    if (!lastSaved) return;
    setSupportEmail(lastSaved.supportEmail);
    setExternalUrls(lastSaved.externalUrls);
    setError(null);
    setSuccessMessage(null);
  }, [lastSaved]);

  const isDirty =
    lastSaved !== null &&
    (supportEmail !== lastSaved.supportEmail ||
      JSON.stringify(externalUrls) !== JSON.stringify(lastSaved.externalUrls));

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        {isDirty && (
          <button
            type="button"
            className="btn btn-outline-secondary me-2"
            onClick={handleDiscard}
            disabled={saving}
          >
            <IconRefresh className="icon me-1" />
            Discard
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || loading}
        >
          <IconDeviceFloppy className="icon me-1" />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success" role="alert">
          {successMessage}
        </div>
      )}
      {lastSaved && (
        <div className="text-muted small mb-3">
          Last saved: {new Date(lastSaved.updatedAt).toLocaleString()} by {lastSaved.updatedBy}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          <div className="col-12 col-lg-6">
            {/* Support email */}
            <div className="card mb-4">
              <div className="card-header">
                <h3 className="card-title">Support Contact</h3>
              </div>
              <div className="card-body">
                <label className="form-label" htmlFor="support-email">
                  Technical support email
                </label>
                <input
                  id="support-email"
                  type="email"
                  className="form-control"
                  value={supportEmail}
                  onChange={e => setSupportEmail(e.target.value)}
                  placeholder="suporte@2bforest.pt"
                />
                <div className="form-text">
                  Shown as a clickable link in the mobile app&apos;s About screen.
                </div>
              </div>
            </div>

            {/* External URLs */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">External URLs</h3>
              </div>
              <div className="card-body">
                <div className="form-text mb-3">
                  URLs opened in the in-app WebView from the Home and Websites screens.
                </div>

                {/* 2BForest URL */}
                <fieldset className="mb-4">
                  <legend className="form-label fw-semibold">2BForest</legend>
                  <div className="mb-2">
                    <label className="form-label" htmlFor="url-2bforest-name">
                      Display name
                    </label>
                    <input
                      id="url-2bforest-name"
                      type="text"
                      className="form-control"
                      value={externalUrls['2bforest'].name}
                      onChange={e =>
                        setExternalUrls(prev => ({
                          ...prev,
                          '2bforest': { ...prev['2bforest'], name: e.target.value },
                        }))
                      }
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label" htmlFor="url-2bforest-url">
                      URL
                    </label>
                    <input
                      id="url-2bforest-url"
                      type="url"
                      className="form-control"
                      value={externalUrls['2bforest'].url}
                      onChange={e =>
                        setExternalUrls(prev => ({
                          ...prev,
                          '2bforest': { ...prev['2bforest'], url: e.target.value },
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="url-2bforest-color">
                      Top bar colour
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        id="url-2bforest-color"
                        type="color"
                        className="form-control form-control-color"
                        value={externalUrls['2bforest'].topBarColor ?? '#101828'}
                        onChange={e =>
                          setExternalUrls(prev => ({
                            ...prev,
                            '2bforest': { ...prev['2bforest'], topBarColor: e.target.value },
                          }))
                        }
                      />
                      <code className="text-muted small">
                        {externalUrls['2bforest'].topBarColor ?? '#101828'}
                      </code>
                    </div>
                  </div>
                </fieldset>

                {/* Forest Sim URL */}
                <fieldset>
                  <legend className="form-label fw-semibold">Forest Sim</legend>
                  <div className="mb-2">
                    <label className="form-label" htmlFor="url-forest-sim-name">
                      Display name
                    </label>
                    <input
                      id="url-forest-sim-name"
                      type="text"
                      className="form-control"
                      value={externalUrls['forest-sim'].name}
                      onChange={e =>
                        setExternalUrls(prev => ({
                          ...prev,
                          'forest-sim': { ...prev['forest-sim'], name: e.target.value },
                        }))
                      }
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label" htmlFor="url-forest-sim-url">
                      URL
                    </label>
                    <input
                      id="url-forest-sim-url"
                      type="url"
                      className="form-control"
                      value={externalUrls['forest-sim'].url}
                      onChange={e =>
                        setExternalUrls(prev => ({
                          ...prev,
                          'forest-sim': { ...prev['forest-sim'], url: e.target.value },
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="url-forest-sim-color">
                      Top bar colour
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        id="url-forest-sim-color"
                        type="color"
                        className="form-control form-control-color"
                        value={externalUrls['forest-sim'].topBarColor ?? '#F39200'}
                        onChange={e =>
                          setExternalUrls(prev => ({
                            ...prev,
                            'forest-sim': {
                              ...prev['forest-sim'],
                              topBarColor: e.target.value,
                            },
                          }))
                        }
                      />
                      <code className="text-muted small">
                        {externalUrls['forest-sim'].topBarColor ?? '#F39200'}
                      </code>
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Main SettingsPage ────────────────────────────────────────────────────────

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('emailTemplate');

  return (
    <div className="page-header d-print-none">
      <div className="row align-items-center">
        <div className="col">
          <div className="page-pretitle">Configuration</div>
          <h2 className="page-title">Settings</h2>
        </div>
      </div>

      <ul className="nav nav-tabs mb-4 mt-3">
        <li className="nav-item">
          <button
            className={`nav-link${activeTab === 'emailTemplate' ? ' active' : ''}`}
            onClick={() => setActiveTab('emailTemplate')}
          >
            Email Template
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${activeTab === 'general' ? ' active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General
          </button>
        </li>
      </ul>

      {activeTab === 'emailTemplate' && <EmailTemplateTab />}
      {activeTab === 'general' && <GeneralSettingsTab />}
    </div>
  );
}
